import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { Link } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { UIProvider, type UILocale } from '@vnetwork/ui/provider';
import { TooltipProvider } from '@vnetwork/ui/primitives';
import { ToastProvider, ConfirmHost, ErrorBoundary } from '@vnetwork/ui/feedback';

import App from '@/App';
import i18n from '@/lib/i18n';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/auth/AuthProvider';
import { initSentry, reportErrorBoundary } from '@/lib/sentry';
import { resolveInitialTheme, persistTheme } from '@/lib/theme-preference';

// Self-hosted base fonts (bundled, served same-origin) so the UI never blocks on
// / falls back from the Google Fonts CDN on flaky links.
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import './styles/global.css';

initSentry();

const toUILocale = (lang: string): UILocale => (lang.startsWith('vi') ? 'vi' : 'en');

/**
 * UIProvider bound to the i18next language, so the design system's built-in
 * strings (calendar, pagination, aria labels, number separators) follow the
 * language switcher instead of staying English.
 */
function AppUIProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<UILocale>(() => toUILocale(i18n.language));
  useEffect(() => {
    const onChange = (lang: string) => setLocale(toUILocale(lang));
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, []);
  return (
    <UIProvider
      defaultTheme={resolveInitialTheme()}
      onThemeChange={persistTheme}
      linkComponent={Link}
      locale={locale}
    >
      {children}
    </UIProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    <AppUIProvider>
      <ErrorBoundary onError={reportErrorBoundary}>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <TooltipProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
                <ConfirmHost />
              </TooltipProvider>
            </ToastProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </ErrorBoundary>
    </AppUIProvider>
  </StrictMode>,
);
